/**
 * A review's list of threads — the panel's body (session 55, `.docs/55-mockup-comments.md`
 * §The review).
 *
 * *Open* and *Resolved*, each in the order the threads were started — which for a page read top
 * to bottom is near enough the page's order, and is the order the pins are numbered in. A row
 * says who, what, the text it is about and how many answered; and where it is when that is not
 * simply here — another page, not on the page at this width, or the whole version. Pressing it
 * opens the thread and brings its pin into view.
 */

import type { MockupCommentView } from '@fareast/shared'

import { Seg, SegItem } from '../../components/portal/Button.js'
import { Empty } from '../../components/portal/Disclosure.js'
import { PinNo, ThreadRow, type PreviewWidth } from '../../components/portal/Files.js'
import { Stack } from '../../components/portal/Layout.js'
import { B, Muted, Small, Xs } from '../../components/portal/Text.js'
import { useFormat } from '../../format/useFormat.js'
import { excerptOf, isOpenThread, whereOf, type ThreadWhere } from './pins.js'
import { WIDTH_WORDS, repliesWord } from './words.js'

export type ThreadTab = 'open' | 'resolved'

export function ThreadList({
  threads,
  numberOf,
  openId,
  tab,
  onTab,
  looking,
  onOpen,
}: {
  /** The version's threads, oldest first. */
  readonly threads: readonly MockupCommentView[]
  readonly numberOf: ReadonlyMap<string, number>
  readonly openId: string | null
  readonly tab: ThreadTab
  readonly onTab: (tab: ThreadTab) => void
  /** What the frame is showing — for where a thread is when it is not simply here. */
  readonly looking: {
    readonly page: string | null
    readonly shown: ReadonlySet<number>
    readonly width: PreviewWidth
  }
  readonly onOpen: (thread: MockupCommentView) => void
}) {
  const open = threads.filter(isOpenThread)
  // Resolved, and — for the studio, the one side that sees them — hidden.
  const resolved = threads.filter((one) => !isOpenThread(one))
  const listed = tab === 'open' ? open : resolved

  return (
    <Stack gap="tight">
      <Seg label="Which comments">
        <SegItem
          on={tab === 'open'}
          onClick={() => {
            onTab('open')
          }}
        >
          {`Open · ${String(open.length)}`}
        </SegItem>
        <SegItem
          on={tab === 'resolved'}
          onClick={() => {
            onTab('resolved')
          }}
        >
          {`Resolved · ${String(resolved.length)}`}
        </SegItem>
      </Seg>
      {listed.length === 0 ? (
        <Empty>
          {tab === 'open' ? 'No open comments on this version.' : 'Nothing resolved yet.'}
        </Empty>
      ) : (
        <Stack gap="tight">
          {listed.map((thread) => {
            const n = numberOf.get(thread.id)
            return (
              <ThreadRow
                key={thread.id}
                pin={n === undefined ? null : <PinNo n={n} done={!isOpenThread(thread)} />}
                on={thread.id === openId}
                done={!isOpenThread(thread)}
                onClick={() => {
                  onOpen(thread)
                }}
              >
                <ThreadSummary thread={thread} where={whereOf(thread, n, looking)} />
              </ThreadRow>
            )
          })}
        </Stack>
      )}
    </Stack>
  )
}

/** One row's words: who and when, what, about what, and where when that is not here. */
function ThreadSummary({
  thread,
  where,
}: {
  readonly thread: MockupCommentView
  readonly where: ThreadWhere
}) {
  const format = useFormat()
  const quote = thread.pin?.quote
  const place = placeWords(where)
  return (
    <>
      <Small>
        <B>{thread.author}</B>
        <Muted>
          {' '}
          &middot; {format.date(thread.created_at)}
          {thread.replies.length === 0 ? '' : ` · ${repliesWord(thread.replies.length)}`}
        </Muted>
      </Small>
      {quote === undefined ? null : (
        <Xs>
          <Muted>&ldquo;{excerptOf(quote, 80)}&rdquo;</Muted>
        </Xs>
      )}
      <Small>{excerptOf(thread.body)}</Small>
      {thread.hidden_at === null ? null : (
        <Xs>
          <Muted>Hidden — the client and the link do not see it.</Muted>
        </Xs>
      )}
      {place === null ? null : (
        <Xs>
          <Muted>{place}</Muted>
        </Xs>
      )}
    </>
  )
}

/** Where a thread is, said — or null when it is simply here. */
export function placeWords(where: ThreadWhere): string | null {
  switch (where.kind) {
    case 'here':
      return null
    case 'version':
      return 'On the whole version'
    case 'page':
      return `On ${where.page}`
    case 'hidden':
      return where.width === null
        ? 'Not on the page at this width'
        : `Left at ${WIDTH_WORDS[where.width].toLowerCase()} width`
  }
}
